import { useState, useMemo } from 'react';
import { NotificationThread } from './types';
import { DEACTIVATED_CHANNEL_ID } from './constants';

/**
 * Custom hook for managing notification filters
 */
export const useNotificationFilters = () => {
    const [searchFilter, setSearchFilter] = useState(false);
    const [isChannelFilterOpen, setIsChannelFilterOpen] = useState(false);
    const [isDeactivateNotificationFilter, setIsDeactivateNotificationFilter] = useState(false);

    return {
        searchFilter,
        setSearchFilter,
        isChannelFilterOpen,
        setIsChannelFilterOpen,
        isDeactivateNotificationFilter,
        setIsDeactivateNotificationFilter,
    };
};

/**
 * Custom hook for filtering notifications based on selected filters
 */
export const useFilteredNotifications = (
    notifications: NotificationThread[],
    selectedChannelFilter: string[],
    isDeactivateNotificationFilter: boolean
) => {
    return useMemo(() => {
        let filtered = notifications;

        // Filter by deactivated status
        if (isDeactivateNotificationFilter) {
            filtered = filtered.filter(
                (notification) => notification.notificationThreadStatus === 'INACTIVE'
            );
        }

        // Filter by channels
        if (selectedChannelFilter.length > 0) {
            if (selectedChannelFilter.includes(DEACTIVATED_CHANNEL_ID)) {
                // Filter for deactivated notifications first
                let onlyDeactivatedNoti = notifications.filter(
                    (notification) => notification.notificationThreadStatus === 'INACTIVE'
                );

                if (selectedChannelFilter.length === 1) {
                    filtered = onlyDeactivatedNoti;
                } else {
                    filtered = onlyDeactivatedNoti.filter((notification) => {
                        const channels = notification.channels.split(',');
                        return selectedChannelFilter.some((r) => channels.includes(r));
                    });
                }
            } else {
                filtered = filtered.filter((notification) => {
                    const channels = notification.channels.split(',');
                    return selectedChannelFilter.some((r) => channels.includes(r));
                });
            }
        }

        return filtered;
    }, [notifications, selectedChannelFilter, isDeactivateNotificationFilter]);
};

/**
 * Custom hook for calculating scroll container height
 */
export const useScrollHeight = (
    selectedChannelFilter: string[],
    searchFilter: boolean
) => {
    return useMemo(() => {
        const SIDEBAR_INITIAL_HEIGHT = 57;
        const FILTER_HEIGHT = 50;
        const SEARCH_HEIGHT = 50;

        const reducedHeight =
            (selectedChannelFilter.length > 0 ? FILTER_HEIGHT : 0) +
            (searchFilter ? SEARCH_HEIGHT : 0) +
            SIDEBAR_INITIAL_HEIGHT;

        return reducedHeight;
    }, [selectedChannelFilter.length, searchFilter]);
};
